import axios from "axios"
import React, { useEffect, useMemo, useState } from "react"

// ─── Helpers ─────────────────────────────────────────────────────────────────
const DOT_ICON = {
  done: "pi pi-check",
  active: "pi pi-clock",
  pending: "pi pi-minus",
  not_started: "pi pi-minus",
  failed: "pi pi-times",
}

const TL_DOT_COLOR = {
  done: "#3B6D11",
  active: "#185FA5",
  pending: "#B4B2A9",
  failed: "#A32D2D",
}

function scoreColor(n) {
  if (n >= 75) return "#639922"
  if (n >= 50) return "#BA7517"
  return "#E24B4A"
}

const toArray = value => {
  if (Array.isArray(value)) return value
  if (Array.isArray(value?.results)) return value.results
  if (Array.isArray(value?.data)) return value.data
  if (Array.isArray(value?.data?.results)) return value.data.results
  return []
}

const firstArray = (...values) => {
  for (const value of values) {
    const arr = toArray(value)
    if (arr.length) return arr
  }
  return []
}

const formatDateTime = value => {
  if (!value) return "--"
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

const getCommunicationBuckets = payload => {
  const data = payload?.data || payload || {}
  const historyRows = toArray(data?.history)

  if (historyRows.length) {
    return historyRows.reduce(
      (acc, item) => {
        const type = String(item?.type || item?.channel || "").toLowerCase()
        if (type.includes("whatsapp")) acc.whatsapp.push(item)
        else if (type.includes("call") || type.includes("phone"))
          acc.calls.push(item)
        else acc.emails.push(item)
        return acc
      },
      { emails: [], whatsapp: [], calls: [] }
    )
  }

  return {
    emails: firstArray(data?.emails, data?.email_history, data?.mail_history),
    whatsapp: firstArray(
      data?.whatsapp,
      data?.whatsapp_history,
      data?.whatsapp_logs
    ),
    calls: firstArray(data?.calls, data?.call_history, data?.call_logs),
  }
}

const normalizeTalentScanResults = data => {
  if (Array.isArray(data?.results)) {
    const nested = data.results.flatMap(item => {
      const nestedResults = item?.talent_scan_result?.results
      return Array.isArray(nestedResults) ? nestedResults : []
    })
    if (nested.length) return nested
  }

  const result = data?.talent_scan_result ?? data?.results ?? data?.data ?? data
  if (Array.isArray(result)) return result
  if (Array.isArray(result?.results)) return result.results
  return []
}

const getTalentScanCandidateId = candidate =>
  candidate?.talent_scan_id ??
  candidate?.candidate_talent_scan_id ??
  candidate?.talent_scan?.id ??
  candidate?.talent_scan_candidate_id ??
  candidate?.candidate_id ??
  candidate?.id

const getCandidateJobId = candidate =>
  candidate?.job_id ??
  candidate?.job?.job_id ??
  candidate?.job?.id ??
  candidate?.job_candidate?.job_id ??
  candidate?.job_candidate_job_id

// ─── Helper: detect if a string is HTML ──────────────────────────────────────
const isHtmlContent = str =>
  typeof str === "string" && /<[a-z][\s\S]*>/i.test(str)

// ─── Safe HTML email renderer ─────────────────────────────────────────────────
function HtmlEmailBody({ html }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 8,
        overflow: "hidden",
        marginTop: 8,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 600,
          color: "#94a3b8",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          padding: "6px 12px",
          background: "#f8fafc",
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        Email Body
      </div>
      <iframe
        srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{margin:0;padding:12px;font-family:inherit;font-size:13px;color:#1e293b;line-height:1.6}img{max-width:100%}a{color:#185FA5}</style></head><body>${html}</body></html>`}
        style={{
          width: "100%",
          border: "none",
          minHeight: 200,
          display: "block",
        }}
        sandbox="allow-same-origin"
        title="Email body"
        onLoad={e => {
          try {
            const doc = e.target.contentDocument
            if (doc) {
              e.target.style.height = doc.documentElement.scrollHeight + "px"
            }
          } catch (_) {}
        }}
      />
    </div>
  )
}

const buildApiStages = ({
  communication,
  talentScanResults,
  candidate,
  aiScreeningResult,
}) => {
  const latestEmail = communication.emails[0]
  const latestWhatsapp = communication.whatsapp[0]
  const latestCall = communication.calls[0]
  const latestTalentScan = talentScanResults[0]
  const talentScore =
    latestTalentScan?.ats_details?.percentage_score ??
    latestTalentScan?.candidate_percentage_score ??
    latestTalentScan?.percentage

  return [
    {
      id: 1,
      name: "Mail",
      icon: "pi pi-envelope",
      status: latestEmail || latestWhatsapp ? "done" : "not_started",
      date: formatDateTime(
        latestEmail?.created_at ||
          latestEmail?.sent_at ||
          latestWhatsapp?.created_at
      ),
      time: "",
      badge: latestEmail || latestWhatsapp ? "Sent" : "Not started",
      history: [...communication.emails, ...communication.whatsapp].map(
        item => ({
          dot: "done",
          title:
            item?.subject ||
            item?.template_name ||
            item?.type ||
            "Message sent",
          time: formatDateTime(
            item?.created_at || item?.sent_at || item?.updated_at
          ),
          desc: item?.status || item?.message || "",
          body: item?.body || item?.html_body || item?.content || "",
          channel: item?.type?.toLowerCase?.().includes("whatsapp")
            ? "WhatsApp"
            : "Email",
        })
      ),
      qa: [],
    },
    {
      id: 2,
      name: "AI Screening",
      icon: "pi pi-android",
      status: aiScreeningResult ? "done" : latestCall ? "done" : "not_started",
      date: formatDateTime(
        aiScreeningResult?.date_of_interview ||
          latestCall?.created_at ||
          latestCall?.calldate ||
          latestCall?.start_time
      ),
      time: "",
      badge: aiScreeningResult
        ? aiScreeningResult.overall_score !== null
          ? `${aiScreeningResult.overall_score}/10`
          : "Completed"
        : latestCall?.call_status || latestCall?.status || "Not started",
      aiScreeningResult,
      latestCall,
      history: communication.calls.map(item => ({
        dot: "done",
        title: item?.call_status || item?.status || "Call activity",
        time: formatDateTime(
          item?.created_at || item?.calldate || item?.start_time
        ),
        desc: item?.comments || item?.summary || item?.recording_url || "",
      })),
    },
    {
      id: 3,
      name: "Talent Scan Test",
      icon: "pi pi-check-square",
      status: talentScanResults.length > 0 ? "done" : "not_started",
      date: formatDateTime(
        talentScanResults[0]?.attempted_at ||
          talentScanResults[0]?.submitted_at ||
          talentScanResults[0]?.evaluated_at
      ),
      time: "",
      badge:
        talentScore !== undefined && talentScore !== null
          ? `${talentScore}%`
          : "Not started",
      allTalentScanResults: talentScanResults,
      history: talentScanResults.map(item => ({
        dot: "done",
        title: item?.test_info?.name || "Talent Scan submitted",
        time: formatDateTime(
          item?.submitted_at || item?.attempted_at || item?.evaluated_at
        ),
        desc: `Score: ${
          item?.ats_details?.percentage_score ??
          item?.candidate_percentage_score ??
          "--"
        }%`,
      })),
      qa: [],
    },
    {
      id: 4,
      name: "Schedule Panel Interview",
      icon: "pi pi-users",
      status:
        candidate?.interview_scheduled || candidate?.scheduled_interview
          ? "active"
          : "not_started",
      date: formatDateTime(
        candidate?.interview_date || candidate?.scheduled_interview?.start_date
      ),
      time: candidate?.interview_time || "",
      badge:
        candidate?.interview_scheduled || candidate?.scheduled_interview
          ? "Scheduled"
          : "Not started",
      interviewPreview: {
        round: candidate?.interview_round || "Panel Interview",
        scheduled: formatDateTime(
          candidate?.interview_date ||
            candidate?.scheduled_interview?.start_date
        ),
        mode: candidate?.interview_mode || "--",
        panel: toArray(candidate?.interview_panel || candidate?.panel_names),
        status:
          candidate?.interview_scheduled || candidate?.scheduled_interview
            ? "Upcoming"
            : "Not scheduled",
      },
      history: toArray(
        candidate?.interview_logs || candidate?.schedule_logs
      ).map(item => ({
        dot: "active",
        title: item?.title || item?.status || "Interview activity",
        time: formatDateTime(item?.created_at || item?.start_date),
        desc: item?.body || item?.comments || "",
      })),
      qa: [],
    },
  ]
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function InfoCard({ rows }) {
  return (
    <div style={styles.infoCard}>
      {rows.map((row, i) => (
        <div
          key={i}
          style={{
            ...styles.kvRow,
            borderBottom: i < rows.length - 1 ? "0.5px solid #e0e0e0" : "none",
          }}
        >
          <span style={styles.kvLabel}>{row.label}</span>
          <span style={{ ...styles.kvValue, ...row.valueStyle }}>
            {row.value}
          </span>
        </div>
      ))}
    </div>
  )
}

function ScoreBar({ label, value, max = 100, color }) {
  const pct = Math.round((value / max) * 100)
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={styles.barLabels}>
        <span>{label}</span>
        <span>{pct}%</span>
      </div>
      <div style={styles.barTrack}>
        <div
          style={{
            ...styles.barFill,
            width: `${pct}%`,
            background: color || scoreColor(pct),
          }}
        />
      </div>
    </div>
  )
}

// ─── Pagination Bar ───────────────────────────────────────────────────────────
function PaginationBar({ label, current, total, onPrev, onNext }) {
  if (total <= 1) return null
  return (
    <div style={styles.paginationBar}>
      <button
        onClick={onPrev}
        disabled={current === 0}
        style={{
          ...styles.paginationBtn,
          opacity: current === 0 ? 0.35 : 1,
          cursor: current === 0 ? "not-allowed" : "pointer",
        }}
      >
        ← Prev
      </button>
      <span style={styles.paginationLabel}>
        {label} <strong>{current + 1}</strong> of <strong>{total}</strong>
      </span>
      <button
        onClick={onNext}
        disabled={current === total - 1}
        style={{
          ...styles.paginationBtn,
          opacity: current === total - 1 ? 0.35 : 1,
          cursor: current === total - 1 ? "not-allowed" : "pointer",
        }}
      >
        Next →
      </button>
    </div>
  )
}

// ─── Top stage stepper ───────────────────────────────────────

function StageStepper({ stages, selectedId, onSelect }) {
  return (
    <div style={styles.topNav}>
      {stages.map((s, idx) => {
        const isSel = selectedId === s.id
        return (
          <React.Fragment key={s.id}>
            <button
              type="button"
              onClick={() => onSelect(s.id)}
              style={{
                ...styles.stepBtn,
                ...(isSel ? styles.stepBtnActive : null),
              }}
            >
              <span style={{ ...styles.stepDot, ...dotStyle(s.status) }}>
                <i
                  className={DOT_ICON[s.status] || "pi pi-minus"}
                  style={{ fontSize: 14 }}
                />
              </span>
              <span style={styles.stepText}>
                <span style={styles.stepName}>{s.name}</span>
                <span style={styles.stepDate}>
                  {s.date !== "—" ? s.date : "—"}
                </span>
              </span>
              <span style={{ ...styles.stepBadge, ...badgeStyle(s.status) }}>
                {s.badge}
              </span>
            </button>
            {idx < stages.length - 1 && <span style={styles.stepConnector} />}
          </React.Fragment>
        )
      })}
    </div>
  )
}

// ─── Stage-specific content panels ───────────────────────────────────────────

function MailStageContent({ stage }) {
  const entries = stage.history || []
  if (!entries.length) {
    return (
      <div style={styles.empty}>
        <i
          className="pi pi-envelope"
          style={{
            fontSize: 28,
            opacity: 0.3,
            display: "block",
            marginBottom: 8,
          }}
        />
        No mail activity yet
      </div>
    )
  }

  return (
    <>
      <p style={styles.secLabel}>Mail activity</p>
      {entries.map((e, i) => (
        <div key={i} style={{ marginBottom: 16 }}>
          <div style={styles.tlEntry}>
            <div style={styles.tlLeft}>
              <div
                style={{
                  ...styles.tlDot,
                  background: TL_DOT_COLOR[e.dot] || "#B4B2A9",
                }}
              />
              {(i < entries.length - 1 || e.body) && (
                <div style={styles.tlLine} />
              )}
            </div>
            <div style={styles.tlBody}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={styles.tlTitle}>{e.title}</div>
                {e.channel && (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      padding: "1px 6px",
                      borderRadius: 20,
                      background:
                        e.channel === "WhatsApp" ? "#EAF3DE" : "#E6F1FB",
                      color: e.channel === "WhatsApp" ? "#3B6D11" : "#185FA5",
                    }}
                  >
                    {e.channel}
                  </span>
                )}
              </div>
              <div style={styles.tlTime}>{e.time}</div>
              {e.desc && !isHtmlContent(e.desc) && (
                <div style={styles.tlDesc}>{e.desc}</div>
              )}
            </div>
          </div>
          {e.body && isHtmlContent(e.body) && (
            <div style={{ paddingLeft: 30 }}>
              <HtmlEmailBody html={e.body} />
            </div>
          )}
          {e.body && !isHtmlContent(e.body) && e.body.trim() && (
            <div style={{ paddingLeft: 30 }}>
              <div
                style={{
                  marginTop: 6,
                  padding: "10px 12px",
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  fontSize: 12,
                  color: "#374151",
                  lineHeight: 1.6,
                }}
              >
                {e.body}
              </div>
            </div>
          )}
        </div>
      ))}
    </>
  )
}

// ─── AI Screening — single-column full-width layout ───────────────────────────
function AiScreeningStageContent({
  stage,
  aiScreeningLoading,
  aiScreeningError,
  aiScreeningResults,
  aiScreeningIndex,
  onAiScreeningPrev,
  onAiScreeningNext,
}) {
  const aiResult = aiScreeningResults[aiScreeningIndex] || null
  const latestCall = stage.latestCall

  if (aiScreeningLoading) {
    return (
      <div
        style={{ textAlign: "center", padding: "48px 20px", color: "#94a3b8" }}
      >
        <i
          className="pi pi-spin pi-spinner"
          style={{ fontSize: 28, display: "block", marginBottom: 8 }}
        />
        Loading AI screening result...
      </div>
    )
  }

  if (!aiResult && !latestCall && aiScreeningError) {
    return (
      <div
        style={{ textAlign: "center", padding: "48px 20px", color: "#A32D2D" }}
      >
        <i
          className="pi pi-times-circle"
          style={{ fontSize: 28, display: "block", marginBottom: 8 }}
        />
        {aiScreeningError}
      </div>
    )
  }

  if (!aiResult && !latestCall) {
    return (
      <div style={styles.empty}>
        <i
          className="pi pi-lock"
          style={{
            fontSize: 28,
            opacity: 0.3,
            display: "block",
            marginBottom: 8,
          }}
        />
        Stage not started yet
      </div>
    )
  }

  return (
    <>
      {/* Pagination */}
      {aiScreeningResults.length > 1 && (
        <PaginationBar
          label="Call"
          current={aiScreeningIndex}
          total={aiScreeningResults.length}
          onPrev={onAiScreeningPrev}
          onNext={onAiScreeningNext}
        />
      )}

      {aiResult ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            width: "100%",
          }}
        >
          {/* ── Overall Score ── */}
          {aiResult.overall_score !== null &&
            aiResult.overall_score !== undefined && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: "12px 16px",
                  background:
                    "linear-gradient(135deg, #eff6ff 0%, #e0f2fe 100%)",
                  borderRadius: 10,
                  border: "1px solid #bfdbfe",
                }}
              >
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontSize: 20,
                    fontWeight: 800,
                    flexShrink: 0,
                    boxShadow: "0 2px 8px rgba(37,99,235,0.25)",
                  }}
                >
                  {aiResult.overall_score}
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 10,
                      color: "#6b7280",
                      textTransform: "uppercase",
                      fontWeight: 600,
                      marginBottom: 2,
                    }}
                  >
                    Overall Score
                  </div>
                  <div
                    style={{ fontSize: 15, fontWeight: 700, color: "#1e40af" }}
                  >
                    {aiResult.overall_score}/10
                  </div>
                  {aiResult.date_of_interview &&
                    aiResult.date_of_interview !== "-" && (
                      <div
                        style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}
                      >
                        {formatDateTime(aiResult.date_of_interview)}
                      </div>
                    )}
                </div>
              </div>
            )}

          {/* ── Section scores — 2-col grid ── */}
          {aiResult.sections && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                gap: 8,
              }}
            >
              {Object.entries(aiResult.sections).map(([key, section], idx) => (
                <div
                  key={idx}
                  style={{
                    padding: "10px 12px",
                    background: "#f8fafc",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 4,
                    }}
                  >
                    <span style={{ fontSize: 12, fontWeight: 600 }}>
                      {section.section_name || key}
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        padding: "2px 8px",
                        borderRadius: 20,
                        background:
                          section.score >= 8
                            ? "#EAF3DE"
                            : section.score >= 6
                            ? "#FAEEDA"
                            : "#FCEBEB",
                        color:
                          section.score >= 8
                            ? "#3B6D11"
                            : section.score >= 6
                            ? "#854F0B"
                            : "#A32D2D",
                      }}
                    >
                      {section.score}/10
                    </span>
                  </div>
                  <div
                    style={{
                      width: "100%",
                      height: 5,
                      borderRadius: 3,
                      background: "#e5e7eb",
                    }}
                  >
                    <div
                      style={{
                        width: `${(section.score / 10) * 100}%`,
                        height: "100%",
                        borderRadius: 3,
                        background:
                          section.score >= 8
                            ? "#059669"
                            : section.score >= 6
                            ? "#d97706"
                            : "#dc2626",
                      }}
                    />
                  </div>
                  {section.feedback && (
                    <div
                      style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}
                    >
                      {section.feedback}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── Key Strengths ── */}
          {aiResult.key_strengths && (
            <div
              style={{
                padding: "8px 12px",
                background: "#ecfdf5",
                borderRadius: 8,
                border: "1px solid #a7f3d0",
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#059669",
                  textTransform: "uppercase",
                }}
              >
                Key Strengths
              </div>
              <div style={{ fontSize: 12, color: "#065f46", marginTop: 2 }}>
                {aiResult.key_strengths}
              </div>
            </div>
          )}

          {/* ── Priority Improvements ── */}
          {aiResult.priority_improvements && (
            <div
              style={{
                padding: "8px 12px",
                background: "#fffbeb",
                borderRadius: 8,
                border: "1px solid #fde68a",
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#d97706",
                  textTransform: "uppercase",
                }}
              >
                Priority Improvements
              </div>
              <div style={{ fontSize: 12, color: "#92400e", marginTop: 2 }}>
                {aiResult.priority_improvements}
              </div>
            </div>
          )}

          {/* ── Recommendation ── */}
          {aiResult.overall_recommendation && (
            <div
              style={{
                padding: "8px 12px",
                background: "#eff6ff",
                borderRadius: 8,
                border: "1px solid #bfdbfe",
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#2563eb",
                  textTransform: "uppercase",
                }}
              >
                Recommendation
              </div>
              <div style={{ fontSize: 12, color: "#1e40af", marginTop: 2 }}>
                {aiResult.overall_recommendation}
              </div>
            </div>
          )}

          {/* ── Call Recording — full width ── */}
          {aiResult.recording && (
            <div>
              <div style={styles.secLabel}>Call Recording</div>
              <audio controls style={{ width: "100%", height: 36 }}>
                <source src={aiResult.recording} type="audio/wav" />
              </audio>
            </div>
          )}

          {/* ── Transcript — full width ── */}
          {aiResult.transcript && aiResult.transcript.length > 0 && (
            <div>
              <div style={styles.secLabel}>
                Transcript ({aiResult.transcript.length} messages)
              </div>
              <div style={styles.transcriptBox}>
                {aiResult.transcript.map((msg, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      justifyContent:
                        msg.role === "ai" ? "flex-start" : "flex-end",
                      marginBottom: 8,
                    }}
                  >
                    <div
                      style={{
                        maxWidth: "75%",
                        padding: "8px 12px",
                        borderRadius:
                          msg.role === "ai"
                            ? "4px 12px 12px 12px"
                            : "12px 4px 12px 12px",
                        background: msg.role === "ai" ? "#fff" : "#185FA5",
                        color: msg.role === "ai" ? "#1f2937" : "#fff",
                        fontSize: 12,
                        border:
                          msg.role === "ai" ? "1px solid #e5e7eb" : "none",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          opacity: 0.6,
                          marginBottom: 3,
                        }}
                      >
                        {msg.role === "ai" ? "AI Recruiter" : "Candidate"}
                      </div>
                      {msg.content}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : latestCall ? (
        // Fallback: plain call data
        <>
          <p style={styles.secLabel}>AI screening result</p>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 6,
              marginBottom: 10,
            }}
          >
            <span style={styles.scoreBig}>{latestCall?.score ?? 0}</span>
            <span style={styles.scoreDenom}>/ 100</span>
          </div>
          <ScoreBar label="Score" value={latestCall?.score ?? 0} />
          <InfoCard
            rows={[
              { label: "Agent", value: latestCall?.agent_name || "--" },
              { label: "Duration", value: latestCall?.duration || "--" },
              {
                label: "Sentiment",
                value: latestCall?.sentiment || "--",
                valueStyle: { color: "#3B6D11" },
              },
              {
                label: "Verdict",
                value: latestCall?.call_status || "--",
                valueStyle: { color: "#3B6D11" },
              },
            ]}
          />
        </>
      ) : null}

      {/* Call history timeline */}
      {stage.history?.length > 0 && (
        <>
          <p style={{ ...styles.secLabel, marginTop: 14 }}>
            Call activity timeline
          </p>
          {stage.history.map((e, i) => (
            <div key={i} style={styles.tlEntry}>
              <div style={styles.tlLeft}>
                <div
                  style={{
                    ...styles.tlDot,
                    background: TL_DOT_COLOR[e.dot] || "#B4B2A9",
                  }}
                />
                {i < stage.history.length - 1 && <div style={styles.tlLine} />}
              </div>
              <div style={styles.tlBody}>
                <div style={styles.tlTitle}>{e.title}</div>
                <div style={styles.tlTime}>{e.time}</div>
                {e.desc && <div style={styles.tlDesc}>{e.desc}</div>}
              </div>
            </div>
          ))}
        </>
      )}
    </>
  )
}

// ─── Talent Scan — simplified with just summary and button, no history ──────────────────────
function TalentScanStageContent({ stage, onViewTestResults }) {
  const allResults = stage.allTalentScanResults || []

  if (!allResults.length) {
    return (
      <div style={styles.empty}>
        <i
          className="pi pi-lock"
          style={{
            fontSize: 28,
            opacity: 0.3,
            display: "block",
            marginBottom: 8,
          }}
        />
        Stage not started yet
      </div>
    )
  }

  // Derive a quick summary from the first result for the card
  const ts = allResults[0]
  const talentScore =
    ts?.ats_details?.percentage_score ?? ts?.candidate_percentage_score ?? 0
  const score = Number(talentScore)
  const qualified = ts?.ats_details?.is_test_qualified
  const qualifiedColor = qualified ? "#3B6D11" : "#A32D2D"
  const qualifiedBg = qualified ? "#EAF3DE" : "#FCEBEB"

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 24px",
        gap: 20,
      }}
    >
      {/* Icon */}
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          background: "#E6F1FB",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 32,
        }}
      >
        📋
      </div>

      {/* Count */}
      <div style={{ textAlign: "center" }}>
        <div
          style={{
            fontSize: 17,
            fontWeight: 700,
            color: "#1e293b",
            marginBottom: 4,
          }}
        >
          {allResults.length} Test{allResults.length > 1 ? "s" : ""} Available
        </div>
        <div style={{ fontSize: 13, color: "#94a3b8", marginBottom: 16 }}>
          View detailed scores, section breakdown, and attempt history.
        </div>
      </div>

      {/* Quick summary chips */}
      <div
        style={{
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: 10,
            padding: "10px 20px",
            textAlign: "center",
            minWidth: 100,
          }}
        >
          <div
            style={{
              fontSize: 10,
              color: "#94a3b8",
              textTransform: "uppercase",
              marginBottom: 4,
            }}
          >
            Latest Score
          </div>
          <div
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: scoreColor(score),
            }}
          >
            {score}%
          </div>
        </div>

        <div
          style={{
            background: qualifiedBg,
            border: `1px solid ${qualifiedColor}33`,
            borderRadius: 10,
            padding: "10px 20px",
            textAlign: "center",
            minWidth: 100,
          }}
        >
          <div
            style={{
              fontSize: 10,
              color: qualifiedColor,
              textTransform: "uppercase",
              marginBottom: 4,
            }}
          >
            Result
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: qualifiedColor }}>
            {qualified ? "Qualified" : "Not Qualified"}
          </div>
        </div>

        <div
          style={{
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: 10,
            padding: "10px 20px",
            textAlign: "center",
            minWidth: 100,
          }}
        >
          <div
            style={{
              fontSize: 10,
              color: "#94a3b8",
              textTransform: "uppercase",
              marginBottom: 4,
            }}
          >
            Total Tests
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#185FA5" }}>
            {allResults.length}
          </div>
        </div>
      </div>

      {/* CTA button — triggers the TestResultsModal from JobOrdersView */}
      <button
        onClick={() => onViewTestResults(allResults)}
        style={{
          background: "#185FA5",
          color: "#fff",
          border: "none",
          borderRadius: 8,
          padding: "11px 32px",
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginTop: 4,
          boxShadow: "0 2px 8px rgba(24,95,165,0.18)",
        }}
      >
        <i className="pi pi-eye" style={{ fontSize: 14 }} />
        View Detailed Test Results
      </button>
    </div>
  )
}

function InterviewStageContent({ stage, onScheduleInterview }) {
  const p = stage.interviewPreview
  const historyEntries = stage.history || []

  return (
    <>
      {p && (
        <>
          <p style={styles.secLabel}>Interview details</p>
          <InfoCard
            rows={[
              { label: "Round", value: p.round },
              { label: "Scheduled", value: p.scheduled },
              { label: "Mode", value: p.mode, valueStyle: { fontSize: 11 } },
              {
                label: "Panel",
                value: (p.panel || []).join(", "),
                valueStyle: { fontSize: 11 },
              },
              {
                label: "Status",
                value: p.status,
                valueStyle: { color: "#185FA5" },
              },
            ]}
          />
          <div style={styles.infoBanner}>
            <i className="pi pi-info-circle" style={{ fontSize: 13 }} />
            {p.status === "Not scheduled"
              ? "Schedule the panel interview from this stage."
              : "Interview details are available for this candidate."}
          </div>
          {onScheduleInterview && (
            <button
              type="button"
              onClick={onScheduleInterview}
              style={styles.actionButton}
            >
              Schedule Interview
            </button>
          )}
        </>
      )}

      {historyEntries.length > 0 && (
        <>
          <p style={{ ...styles.secLabel, marginTop: p ? 16 : 0 }}>
            Activity timeline
          </p>
          {historyEntries.map((e, i) => (
            <div key={i} style={styles.tlEntry}>
              <div style={styles.tlLeft}>
                <div
                  style={{
                    ...styles.tlDot,
                    background: TL_DOT_COLOR[e.dot] || "#B4B2A9",
                  }}
                />
                {i < historyEntries.length - 1 && <div style={styles.tlLine} />}
              </div>
              <div style={styles.tlBody}>
                <div style={styles.tlTitle}>{e.title}</div>
                <div style={styles.tlTime}>{e.time}</div>
                {e.desc && <div style={styles.tlDesc}>{e.desc}</div>}
              </div>
            </div>
          ))}
        </>
      )}

      {!p && !historyEntries.length && (
        <div style={styles.empty}>
          <i
            className="pi pi-lock"
            style={{
              fontSize: 28,
              opacity: 0.3,
              display: "block",
              marginBottom: 8,
            }}
          />
          Stage not started yet
        </div>
      )}
    </>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * Props
 * ─────
 * candidate           – the candidate object
 * companyId           – optional company ID
 * onScheduleInterview – callback for the Schedule Interview button (stage 4)
 * onViewTestResults   – callback to open the TestResultsModal from JobOrdersView (stage 3)
 * stages              – optional override stages array
 */
const AIResultPanel = ({
  candidate,
  companyId,
  onScheduleInterview,
  onViewTestResults, // ← PROP for talent scan button
  stages,
}) => {
  const [selStageId, setSelStageId] = useState(null)
  const [communication, setCommunication] = useState({
    emails: [],
    whatsapp: [],
    calls: [],
  })
  const [talentScanResults, setTalentScanResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [aiScreeningLoading, setAiScreeningLoading] = useState(false)
  const [aiScreeningError, setAiScreeningError] = useState("")
  const [aiScreeningResults, setAiScreeningResults] = useState([])
  const [aiScreeningResultIndex, setAiScreeningResultIndex] = useState(0)
  const aiScreeningResult = aiScreeningResults[aiScreeningResultIndex] || null
  const [talentScanIndex, setTalentScanIndex] = useState(0)

  useEffect(() => {
    let mounted = true
    setAiScreeningResults([])
    setAiScreeningResultIndex(0)
    setAiScreeningError("")
    setAiScreeningLoading(false)
    setTalentScanIndex(0)
    setSelStageId(null)

    const jobCandidateId =
      candidate?.job_candidate_id ||
      candidate?.candidate_job_id ||
      candidate?.id
    const talentScanId = getTalentScanCandidateId(candidate)
    const jobId = getCandidateJobId(candidate)

    setCommunication({ emails: [], whatsapp: [], calls: [] })
    setTalentScanResults(
      normalizeTalentScanResults(candidate?.talent_scan_result)
    )

    if (!candidate) return undefined

    const fetchStageData = async () => {
      setLoading(true)
      try {
        const requests = []

        if (jobCandidateId) {
          requests.push(
            axios
              .get(`/api/v1/candidate-communication-history/${jobCandidateId}/`)
              .then(response => ({ type: "communication", response }))
          )
        }

        if (jobId && talentScanId) {
          requests.push(
            axios
              .post(
                `/api/v1/candidate-talent-scan-result/`,
                { talent_scan_id: talentScanId, job_id: jobId },
                {
                  params: {
                    page: 1,
                    size: 10,
                    ...(companyId ? { company_id: companyId } : {}),
                  },
                }
              )
              .then(response => ({ type: "talentScan", response }))
          )
        }

        const callLogs = candidate?.call_logs || []
        const allExternalCallIds = []
        for (const log of callLogs) {
          const secondaryEvals = log?.secondary_evaluations || []
          for (const eval_ of secondaryEvals) {
            if (
              eval_?.external_call_id &&
              !allExternalCallIds.includes(eval_.external_call_id)
            ) {
              allExternalCallIds.push(eval_.external_call_id)
            }
          }
        }

        if (allExternalCallIds.length > 0) {
          setAiScreeningLoading(true)
          allExternalCallIds.forEach(externalCallId => {
            requests.push(
              axios
                .get(`/api/v1/get-secondary-evaluation/?id=${externalCallId}`)
                .then(response => ({
                  type: "secondaryEval",
                  response,
                  externalCallId,
                }))
                .catch(() => ({
                  type: "secondaryEval",
                  response: null,
                  externalCallId,
                }))
            )
          })
        }

        const responses = await Promise.allSettled(requests)
        if (!mounted) return

        const secondaryEvalResults = []

        responses.forEach(result => {
          if (result.status !== "fulfilled") return

          if (result.value.type === "communication") {
            setCommunication(getCommunicationBuckets(result.value.response))
          }
          if (result.value.type === "talentScan") {
            setTalentScanResults(
              normalizeTalentScanResults(result.value.response?.data)
            )
          }
          if (result.value.type === "secondaryEval") {
            const data = result.value.response?.data
            if (data) {
              const evalData = data?.data || {}
              secondaryEvalResults.push({
                externalCallId: result.value.externalCallId,
                candidate_name:
                  evalData["Candidate Name"] || candidate?.first_name || "-",
                agent_name: evalData["Agent Name"] || "-",
                date_of_interview: evalData.date_of_interview || "-",
                questions: evalData["Questions"] || [],
                transcript: data?.transcript || [],
                recording: data?.recording || "",
                sections: evalData.sections || null,
                overall_score: evalData.overall_score ?? null,
                key_strengths: evalData.key_strengths || "",
                overall_recommendation: evalData.overall_recommendation || "",
                priority_improvements: evalData.priority_improvements || "",
              })
            }
          }
        })

        if (secondaryEvalResults.length > 0) {
          setAiScreeningResults(secondaryEvalResults)
          setAiScreeningResultIndex(0)
        } else if (allExternalCallIds.length > 0) {
          setAiScreeningError("No screening result available.")
        }

        setAiScreeningLoading(false)
      } finally {
        if (mounted) setLoading(false)
      }
    }

    fetchStageData()
    return () => {
      mounted = false
    }
  }, [candidate, companyId])

  const apiStages = useMemo(
    () =>
      stages ||
      buildApiStages({
        communication,
        talentScanResults,
        candidate: candidate || {},
        aiScreeningResult,
      }),
    [stages, communication, talentScanResults, candidate, aiScreeningResult]
  )

  useEffect(() => {
    if (selStageId !== null) return
    if (!apiStages.length) return
    const reversed = [...apiStages].reverse()
    const preferred =
      reversed.find(s => s.status === "done" || s.status === "active") ||
      apiStages[0]
    setSelStageId(preferred.id)
  }, [apiStages])

  const selStage = apiStages.find(s => s.id === selStageId) || null

  const renderStageContent = () => {
    if (!selStage) {
      return (
        <div style={styles.empty}>
          <i
            className="pi pi-th-large"
            style={{
              fontSize: 28,
              opacity: 0.3,
              display: "block",
              marginBottom: 8,
            }}
          />
          Select a stage to view details
        </div>
      )
    }

    if (selStage.id === 1) {
      return <MailStageContent stage={selStage} />
    }

    if (selStage.id === 2) {
      return (
        <AiScreeningStageContent
          stage={selStage}
          aiScreeningLoading={aiScreeningLoading}
          aiScreeningError={aiScreeningError}
          aiScreeningResults={aiScreeningResults}
          aiScreeningIndex={aiScreeningResultIndex}
          onAiScreeningPrev={() =>
            setAiScreeningResultIndex(i => Math.max(0, i - 1))
          }
          onAiScreeningNext={() =>
            setAiScreeningResultIndex(i =>
              Math.min(aiScreeningResults.length - 1, i + 1)
            )
          }
        />
      )
    }

    if (selStage.id === 3) {
      return (
        <TalentScanStageContent
          stage={selStage}
          // Pass through the callback — JobOrdersView wires this to
          // setTestResultsCandidate(candidate) + setTestResultsModal(true)
          onViewTestResults={onViewTestResults}
        />
      )
    }

    if (selStage.id === 4) {
      return (
        <InterviewStageContent
          stage={selStage}
          onScheduleInterview={onScheduleInterview}
        />
      )
    }

    return null
  }

  return (
    <div style={styles.wrap}>
      <StageStepper
        stages={apiStages}
        selectedId={selStageId}
        onSelect={setSelStageId}
      />

      <div style={styles.rightHeader}>
        <div>
          <div style={styles.rhTitle}>
            {selStage ? selStage.name : "Select a stage"}
          </div>
          <div style={styles.rhSub}>
            {selStage
              ? selStage.date !== "—"
                ? `${selStage.date} · ${selStage.time}`
                : "Not started yet"
              : "Choose a stage above"}
          </div>
        </div>
        {selStage && (
          <span style={{ ...styles.overallBadge, ...badgeStyle(selStage.status) }}>
            {selStage.badge}
          </span>
        )}
      </div>

      <div style={styles.body}>
        {loading ? (
          <div style={styles.empty}>
            <i
              className="pi pi-spin pi-spinner"
              style={{ fontSize: 28, color: "#185FA5", display: "block", marginBottom: 8 }}
            />
            Loading...
          </div>
        ) : (
          renderStageContent()
        )}
      </div>
    </div>
  )
}

// ─── Status style helpers ─────────────────────────────────────────────────────

function dotStyle(status) {
  const map = {
    done: { background: "#EAF3DE", color: "#3B6D11" },
    active: { background: "#E6F1FB", color: "#185FA5" },
    pending: { background: "#F1EFE8", color: "#888780" },
    not_started: { background: "#F1EFE8", color: "#888780" },
    failed: { background: "#FCEBEB", color: "#A32D2D" },
  }
  return map[status] || map.not_started
}

function badgeStyle(status) {
  const map = {
    done: { background: "#EAF3DE", color: "#3B6D11" },
    active: { background: "#E6F1FB", color: "#185FA5" },
    pending: { background: "#F1EFE8", color: "#5F5E5A" },
    not_started: { background: "#F1EFE8", color: "#5F5E5A" },
    failed: { background: "#FCEBEB", color: "#A32D2D" },
  }
  return map[status] || map.not_started
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  wrap: {
    display: "flex",
    flexDirection: "column",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    overflow: "hidden",
    fontFamily: "inherit",
    fontSize: 13,
    minHeight: 480,
    maxHeight: "78vh",
    background: "#fff",
  },
  topNav: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "12px 16px",
    background: "#fff",
    borderBottom: "1px solid #e2e8f0",
    flexShrink: 0,
    flexWrap: "nowrap", // was "wrap"
    overflow: "hidden",
  },
  stepBtn: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 10px", // was 12px 18px
    borderRadius: 10,
    border: "0.5px solid transparent",
    background: "transparent",
    cursor: "pointer",
    whiteSpace: "nowrap",
    flex: "1 1 0%", // was flexShrink: 0 — now shares space evenly
    minWidth: 0,
    overflow: "hidden",
    transition: "background 0.15s, border-color 0.15s",
  },
  stepBtnActive: {
    background: "#fff",
    border: "0.5px solid #185FA5",
    boxShadow: "0 0 0 3px rgba(24,95,165,0.08)",
  },
  stepDot: {
    width: 28, // was 34
    height: 28,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  stepText: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    lineHeight: 1.2,
    minWidth: 0,
    overflow: "hidden",
  },
  stepName: {
    fontSize: 12, // was 13
    fontWeight: 600,
    color: "#1e293b",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
  },
  stepDate: {
    fontSize: 10, // was 11
    color: "#94a3b8",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
  },
  stepBadge: {
    fontSize: 11,
    padding: "3px 10px",
    borderRadius: 20,
    fontWeight: 500,
    flexShrink: 0,
  },
  stepConnector: {
    width: 12, // was 28 — shorter so it doesn't push things off
    height: 1.5,
    background: "#e2e8f0",
    flexShrink: 0,
    margin: "0 1px",
    alignSelf: "center",
    display: "block",
  },
  right: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  rightHeader: {
    padding: "12px 16px 10px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  rhTitle: { fontSize: 13, fontWeight: 500, color: "#1e293b" },
  rhSub: { fontSize: 11, color: "#64748b", marginTop: 2 },
  overallBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "3px 9px",
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 500,
  },
  body: { flex: 1, minHeight: 0, overflowY: "auto", padding: "16px 20px" },
  empty: {
    textAlign: "center",
    padding: "48px 20px",
    color: "#94a3b8",
    fontSize: 13,
  },
  transcriptBox: {
    maxHeight: 400,
    overflowY: "auto",
    borderRadius: 8,
    padding: 10,
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
  },
  paginationBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 14,
    padding: "10px 14px",
    background: "linear-gradient(90deg, #eff6ff 0%, #e0f2fe 100%)",
    borderRadius: 10,
    border: "1px solid #bfdbfe",
  },
  paginationBtn: {
    border: "1px solid #93c5fd",
    borderRadius: 6,
    background: "#fff",
    padding: "5px 12px",
    fontSize: 12,
    fontWeight: 600,
    color: "#1d4ed8",
    transition: "background 0.1s",
  },
  paginationLabel: {
    fontSize: 12,
    color: "#1e40af",
    fontWeight: 500,
    flex: 1,
    textAlign: "center",
  },
  infoCard: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "10px 12px",
    marginBottom: 10,
  },
  kvRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "5px 0",
  },
  kvLabel: { fontSize: 12, color: "#64748b" },
  kvValue: {
    fontSize: 12,
    fontWeight: 500,
    color: "#1e293b",
    textAlign: "right",
    maxWidth: "55%",
    wordBreak: "break-word",
  },
  barLabels: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 11,
    color: "#94a3b8",
    marginBottom: 4,
  },
  barTrack: {
    height: 6,
    background: "#e2e8f0",
    borderRadius: 3,
    overflow: "hidden",
  },
  barFill: { height: "100%", borderRadius: 3 },
  scoreBig: { fontSize: 26, fontWeight: 500, color: "#1e293b" },
  scoreDenom: { fontSize: 13, color: "#64748b" },
  secLabel: {
    fontSize: 10,
    fontWeight: 500,
    color: "#94a3b8",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    marginBottom: 10,
  },
  infoBanner: {
    background: "#E6F1FB",
    borderRadius: 8,
    padding: "9px 12px",
    fontSize: 12,
    color: "#0C447C",
    marginTop: 4,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  actionButton: {
    marginTop: 10,
    height: 34,
    border: "1px solid #185FA5",
    borderRadius: 6,
    background: "#185FA5",
    color: "#fff",
    fontSize: 12,
    fontWeight: 600,
    padding: "0 12px",
    cursor: "pointer",
  },
  tlEntry: { display: "flex", gap: 10, marginBottom: 0 },
  tlLeft: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: 20,
    flexShrink: 0,
  },
  tlDot: {
    width: 9,
    height: 9,
    borderRadius: "50%",
    flexShrink: 0,
    marginTop: 4,
  },
  tlLine: { flex: 1, width: 1, background: "#e2e8f0", minHeight: 20 },
  tlBody: { flex: 1, paddingBottom: 14 },
  tlTitle: { fontSize: 12, fontWeight: 500, color: "#1e293b" },
  tlTime: { fontSize: 11, color: "#94a3b8", marginTop: 1 },
  tlDesc: { fontSize: 12, color: "#64748b", marginTop: 4, lineHeight: 1.5 },
  chip: {
    display: "inline-block",
    fontSize: 10,
    padding: "2px 7px",
    borderRadius: 20,
    marginTop: 4,
  },
  qaItem: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "10px 12px",
    marginBottom: 8,
  },
  qaQ: {
    fontSize: 12,
    fontWeight: 500,
    color: "#1e293b",
    marginBottom: 4,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 8,
  },
  qaA: { fontSize: 12, color: "#64748b", lineHeight: 1.5 },
  qaBadge: {
    fontSize: 10,
    padding: "2px 7px",
    borderRadius: 20,
    flexShrink: 0,
  },
}

export default AIResultPanel
